import React from "react";
import PropTypes from "prop-types";
import RadioQuestion from "../../../components/RadioQuestion";
import { YES_NO_OPTIONS } from "../../../constants";
import DynamicCheckboxQuestion from "../../../components/DynamicCheckboxQuestion";
import TextQuestion, { TextareaQuestion } from "../../../components/TextQuestion";
import IntegerQuestion from "../../../components/IntegerQuestion";
import PhoneNumberQuestion from "../../../components/PhoneNumberQuestion";
import EmailAddressQuestion from "../../../components/EmailAddressQuestion";
import MoneyQuestion from "../../../components/MoneyQuestion";
import DateQuestion from "../../../components/DateQuestion";
import YearQuestion from "../../../components/YearQuestion";
import CountryPicker from "../../../components/CountryPicker";
import FileUploadQuestion from "../../../components/FileUploadQuestion";
import { isFieldQuestionVisible } from "../../../shared/informationRequest/fieldVisibility";

const RenderQuestion = (props) => {
  const fieldConfig = props.fieldConfig;
  const optional = fieldConfig.required ? false : true;

  if (!isFieldQuestionVisible(props.doableConfig, fieldConfig, props.responses)) {
    return null;
  }

  if (fieldConfig.field_type === "boolean") {
    return (
      <RadioQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        options={YES_NO_OPTIONS}
        inline={true}
        name={fieldConfig.field_identifier}
        selected={props.selected}
        optionSelected={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please choose an option" : null}
      />
    );
  } else if (fieldConfig.field_type === "radio") {
    return (
      <RadioQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        options={fieldConfig.choices}
        name={fieldConfig.field_identifier}
        selected={props.selected}
        optionSelected={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please choose an option" : null}
      />
    );
  } else if (fieldConfig.field_type === "checkbox") {
    return (
      <DynamicCheckboxQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        options={fieldConfig.choices}
        fieldId={fieldConfig.field_identifier}
        selected={props.selected}
        onChange={props.onChange}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please select all that apply" : null}
      />
    );
  } else if (fieldConfig.field_type === "confirmation") {
    return (
      <DynamicCheckboxQuestion
        question={null}
        hint={fieldConfig.hint}
        options={[{ label: fieldConfig.display_name, value: true }]}
        fieldId={fieldConfig.field_identifier}
        selected={props.selected}
        onChange={props.onChange}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please tick the box below" : null}
      />
    );
  } else if (fieldConfig.field_type === "text") {
    return (
      <TextQuestion
        question={fieldConfig.display_name}
        name={fieldConfig.field_identifier}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please fill this in" : null}
      />
    );
  } else if (fieldConfig.field_type === "postcode") {
    return (
      <TextQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please fill this in" : null}
        size="small"
      />
    );
  } else if (fieldConfig.field_type === "text_area") {
    return (
      <TextareaQuestion
        question={fieldConfig.display_name}
        name={fieldConfig.field_identifier}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please fill this in" : null}
      />
    );
  } else if (fieldConfig.field_type === "integer") {
    return (
      <IntegerQuestion
        question={fieldConfig.display_name}
        name={fieldConfig.field_identifier}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? props.validationErrorMessage || "Please fill this in" : null}
      />
    );
  } else if (fieldConfig.field_type === "phone_number" || fieldConfig.field_type === "mobile_number") {
    return (
      <PhoneNumberQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please fill this in" : null}
      />
    );
  } else if (fieldConfig.field_type === "email_address") {
    return (
      <EmailAddressQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please fill this in" : null}
      />
    );
  } else if (fieldConfig.field_type === "money") {
    return (
      <MoneyQuestion
        question={fieldConfig.display_name}
        name={fieldConfig.field_identifier}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? props.validationErrorMessage || "Please fill this in" : null}
      />
    );
  } else if (
    fieldConfig.field_type === "date" ||
    fieldConfig.field_type === "must_happen_by_date" ||
    fieldConfig.field_type === "date_in_the_past"
  ) {
    return (
      <DateQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        name={fieldConfig.field_identifier}
        onChange={props.onDateChange}
        value={props.value}
        optional={optional}
        validate={props.validateDate}
        validationError={props.hasValidationError ? props.validationErrorMessage || "Please enter a date" : null}
      />
    );
  } else if (fieldConfig.field_type === "year") {
    return (
      <YearQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        value={props.value}
        valueEntered={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please enter a year" : null}
        size="small"
      />
    );
  } else if (fieldConfig.field_type === "document_upload") {
    return (
      <FileUploadQuestion
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        multiple={!!fieldConfig.allow_multiple_files}
        defaultUploads={props.uploads}
        onUploadsChanged={props.onUploadsChanged}
        onUploadFailuresChanged={props.onUploadFailuresChanged}
        onUploadInProgress={props.onUploadInProgress}
        caseRef={props.caseRef}
        doableId={props.doableId}
        validationError={
          props.hasValidationError
            ? props.validationErrorMessage || "No file selected. Click below to select a file."
            : null
        }
      />
    );
  } else if (fieldConfig.field_type === "country") {
    return (
      <CountryPicker
        question={fieldConfig.display_name}
        hint={fieldConfig.hint}
        value={props.value}
        onChange={props.optionSelected}
        optional={optional}
        validate={props.validate}
        validationError={props.hasValidationError ? "Please select a country" : null}
        placeholder="Start typing a country name..."
      />
    );
  } else {
    return <></>;
  }
};

RenderQuestion.propTypes = {
  fieldConfig: PropTypes.object.isRequired,
  doableConfig: PropTypes.object.isRequired,
  responses: PropTypes.object.isRequired,
  hasValidationError: PropTypes.bool.isRequired,
  // Read by date and document upload questions. Other field types use the copy in their branch.
  validationErrorMessage: PropTypes.string,
  // The hook holds whatever shape the field type needs: text and money as strings, a checkbox
  // as a map of choice to boolean, a date as day, month and year parts.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object]),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
  validate: PropTypes.func,
  validateDate: PropTypes.func,
  optionSelected: PropTypes.func,
  onChange: PropTypes.func,
  onDateChange: PropTypes.func,
  uploads: PropTypes.arrayOf(
    PropTypes.shape({
      upload_id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
    }),
  ),
  onUploadsChanged: PropTypes.func,
  onUploadFailuresChanged: PropTypes.func,
  onUploadInProgress: PropTypes.func,
  caseRef: PropTypes.string,
  doableId: PropTypes.string,
};

export default RenderQuestion;
